'use client';

// Panel de Ejecución de sanciones + Archivo definitivo (MÓDULO 5). Se muestra en la
// pestaña "Ejecución" del detalle de expediente cuando el estado está en
// FALLO_EJECUTORIADO / EJECUCION_SANCION / SANCION_CUMPLIDA / ARCHIVO_DEFINITIVO.
// Permite comunicar/crear la sanción, registrar su ejecución, su registro y su
// cumplimiento, y archivar el expediente definitivamente. Muestra las sanciones
// existentes con badges de estado. RBAC por rol de localStorage. Los bloqueos
// procesales del backend (422: BLOQUEO_EJECUTAR_SIN_EJECUTORIA,
// BLOQUEO_ARCHIVO_REQUISITOS) se muestran como error.

import { useEffect, useState } from 'react';
import { Archive, CheckCircle2, FileSignature, ListChecks, PlusCircle, Stamp } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import {
  crearSancion, ejecucionSancion, registroSancion, cumplimientoSancion, archivarDefinitivo,
  sanciones as leerSanciones, idSancion, puedeEjecutarSancion, usuarioActual,
  TIPO_SANCION, TIPO_CUMPLIMIENTO,
  type SancionResumen, type TipoSancion, type TipoCumplimiento,
} from '@/lib/segunda-instancia';
import { getCatalogos } from '@/lib/expedientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const hoy = () => new Date().toISOString().slice(0, 10);

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

function val<T = string>(s: SancionResumen, ...claves: string[]): T | undefined {
  for (const k of claves) {
    const v = s[k];
    if (v !== undefined && v !== null) return v as T;
  }
  return undefined;
}

function badgeBool(ok: boolean | undefined | null, si: string, no: string) {
  return ok
    ? <Badge className="bg-primary text-primary-foreground border-transparent">{si}</Badge>
    : <Badge variant="secondary">{no}</Badge>;
}

// ===========================================================================
// Componente principal
// ===========================================================================

export default function PanelSanciones({ exp, onCambio }: { exp: ExpDetalle; onCambio: () => void }) {
  const [crear, setCrear] = useState(false);
  const [archivar, setArchivar] = useState(false);
  const [ejecucion, setEjecucion] = useState<SancionResumen | null>(null);
  const [registro, setRegistro] = useState<SancionResumen | null>(null);
  const [cumplimiento, setCumplimiento] = useState<SancionResumen | null>(null);

  const rol = usuarioActual()?.rol;
  const puede = puedeEjecutarSancion(rol);
  const lista = leerSanciones(exp);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle>Ejecución de sanciones</CardTitle>
          {puede && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setCrear(true)}>
                <PlusCircle className="size-4" /> Comunicar sanción
              </Button>
              <Button size="sm" variant="outline" onClick={() => setArchivar(true)}>
                <Archive className="size-4" /> Archivar definitivamente
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Estado actual: <Badge variant="secondary">{exp.estado_actual}</Badge>.
            {' '}No se puede ejecutar sin ejecutoria ni archivar sin cumplir los requisitos
            (el backend lo bloquea con error 422).
          </p>
          <Separator />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Autoridad ejecutora</TableHead>
                <TableHead>Comunicación</TableHead>
                <TableHead>Ejecutada</TableHead>
                <TableHead>Registrada</TableHead>
                <TableHead>Cumplida</TableHead>
                {puede && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.map((s) => (
                <TableRow key={idSancion(s)}>
                  <TableCell className="font-medium">{val(s, 'tipo_sancion', 'tipoSancion') ?? '—'}</TableCell>
                  <TableCell>{val(s, 'autoridad_ejecutora', 'autoridadEjecutora') ?? '—'}</TableCell>
                  <TableCell>{fmtFecha(val(s, 'fecha_comunicacion', 'fechaComunicacion'))}</TableCell>
                  <TableCell>{badgeBool(val<boolean>(s, 'ejecutada'), 'Sí', 'No')}</TableCell>
                  <TableCell>{badgeBool(val<boolean>(s, 'registrada'), 'Sí', 'No')}</TableCell>
                  <TableCell>{badgeBool(val<boolean>(s, 'cumplida'), 'Sí', 'No')}</TableCell>
                  {puede && (
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setEjecucion(s)}>
                          <FileSignature className="size-3.5" /> Ejecución
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRegistro(s)}>
                          <Stamp className="size-3.5" /> Registro
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setCumplimiento(s)}>
                          <ListChecks className="size-3.5" /> Cumplimiento
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {lista.length === 0 && (
                <TableRow>
                  <TableCell colSpan={puede ? 7 : 6} className="py-8 text-center text-muted-foreground">
                    Sin sanciones registradas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {crear && (
        <ModalCrearSancion
          exp={exp}
          onClose={() => setCrear(false)}
          onListo={() => { setCrear(false); onCambio(); }}
        />
      )}
      {archivar && (
        <ModalArchivar
          exp={exp}
          onClose={() => setArchivar(false)}
          onListo={() => { setArchivar(false); onCambio(); }}
        />
      )}
      {ejecucion && (
        <ModalEjecucion
          sancion={ejecucion}
          onClose={() => setEjecucion(null)}
          onListo={() => { setEjecucion(null); onCambio(); }}
        />
      )}
      {registro && (
        <ModalRegistro
          sancion={registro}
          onClose={() => setRegistro(null)}
          onListo={() => { setRegistro(null); onCambio(); }}
        />
      )}
      {cumplimiento && (
        <ModalCumplimiento
          sancion={cumplimiento}
          onClose={() => setCumplimiento(null)}
          onListo={() => { setCumplimiento(null); onCambio(); }}
        />
      )}
    </div>
  );
}

// ---- Modal: comunicar / crear sanción -------------------------------------

function ModalCrearSancion({ exp, onClose, onListo }: {
  exp: ExpDetalle; onClose: () => void; onListo: () => void;
}) {
  const [tipoSancion, setTipoSancion] = useState<TipoSancion>('SUSPENSION');
  const [autoridadEjecutora, setAutoridadEjecutora] = useState('');
  const [fechaComunicacion, setFechaComunicacion] = useState(hoy());
  const [comunicacionKey, setComunicacionKey] = useState('');
  const [autoridades, setAutoridades] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    getCatalogos()
      .then((c) => setAutoridades(c.autoridadSegundaInstancia ?? []))
      .catch(() => setAutoridades([]));
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!autoridadEjecutora.trim()) { avisoError('Indique la autoridad ejecutora.'); return; }
    if (!fechaComunicacion) { avisoError('Indique la fecha de comunicación.'); return; }
    setGuardando(true);
    try {
      await crearSancion(exp.id, {
        tipoSancion,
        autoridadEjecutora: autoridadEjecutora.trim(),
        fechaComunicacion,
        ...(comunicacionKey.trim() ? { comunicacionKey: comunicacionKey.trim() } : {}),
      });
      avisoExito('Sanción comunicada.');
      onListo();
    } catch (err) {
      avisoError('No se pudo comunicar la sanción', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PlusCircle className="size-5 text-primary" /> Comunicar sanción</DialogTitle>
          <DialogDescription>Radicado: <strong>{exp.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de sanción</Label>
            <Select value={tipoSancion} onValueChange={(v) => setTipoSancion(v as TipoSancion)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPO_SANCION.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Autoridad ejecutora</Label>
            {autoridades.length ? (
              <Select value={autoridadEjecutora} onValueChange={setAutoridadEjecutora}>
                <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
                <SelectContent>
                  {autoridades.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={autoridadEjecutora} onChange={(e) => setAutoridadEjecutora(e.target.value)} placeholder="Ej: Gobernador" />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="fc">Fecha de comunicación</Label>
            <Input id="fc" type="date" value={fechaComunicacion} onChange={(e) => setFechaComunicacion(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ck">Comunicación (key, opcional)</Label>
            <Input id="ck" value={comunicacionKey} onChange={(e) => setComunicacionKey(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Comunicar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: registrar ejecución -------------------------------------------

function ModalEjecucion({ sancion, onClose, onListo }: {
  sancion: SancionResumen; onClose: () => void; onListo: () => void;
}) {
  const [ejecutada, setEjecutada] = useState<boolean>(!!val<boolean>(sancion, 'ejecutada'));
  const [fechaEjecucion, setFechaEjecucion] = useState('');
  const [actoEjecucionKey, setActoEjecucionKey] = useState('');
  const [numeroActo, setNumeroActo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await ejecucionSancion(idSancion(sancion), {
        ejecutada,
        ...(fechaEjecucion ? { fechaEjecucion } : {}),
        ...(actoEjecucionKey.trim() ? { actoEjecucionKey: actoEjecucionKey.trim() } : {}),
        ...(numeroActo.trim() ? { numeroActo: numeroActo.trim() } : {}),
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      });
      avisoExito('Ejecución registrada.');
      onListo();
    } catch (err) {
      avisoError('No se pudo registrar la ejecución', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="size-5 text-primary" /> Registrar ejecución</DialogTitle>
          <DialogDescription>Sanción: {val(sancion, 'tipo_sancion', 'tipoSancion') ?? '—'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={ejecutada} onChange={(e) => setEjecutada(e.target.checked)} className="size-4 accent-primary" />
            Ejecutada
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fe">Fecha de ejecución</Label>
              <Input id="fe" type="date" value={fechaEjecucion} onChange={(e) => setFechaEjecucion(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="na">Número del acto</Label>
              <Input id="na" value={numeroActo} onChange={(e) => setNumeroActo(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ak">Acto de ejecución (key, opcional)</Label>
            <Input id="ak" value={actoEjecucionKey} onChange={(e) => setActoEjecucionKey(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ob">Observaciones (opcional)</Label>
            <Textarea id="ob" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: registro de sanción -------------------------------------------

function ModalRegistro({ sancion, onClose, onListo }: {
  sancion: SancionResumen; onClose: () => void; onListo: () => void;
}) {
  const [registrada, setRegistrada] = useState<boolean>(!!val<boolean>(sancion, 'registrada'));
  const [soporteRegistroKey, setSoporteRegistroKey] = useState('');
  const [fechaRegistro, setFechaRegistro] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await registroSancion(idSancion(sancion), {
        registrada,
        ...(soporteRegistroKey.trim() ? { soporteRegistroKey: soporteRegistroKey.trim() } : {}),
        ...(fechaRegistro ? { fechaRegistro } : {}),
      });
      avisoExito('Registro de sanción actualizado.');
      onListo();
    } catch (err) {
      avisoError('No se pudo registrar la sanción', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stamp className="size-5 text-primary" /> Registro de sanción</DialogTitle>
          <DialogDescription>Sanción: {val(sancion, 'tipo_sancion', 'tipoSancion') ?? '—'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={registrada} onChange={(e) => setRegistrada(e.target.checked)} className="size-4 accent-primary" />
            Registrada (SIRI / registro correspondiente)
          </label>
          <div className="space-y-2">
            <Label htmlFor="fr">Fecha de registro</Label>
            <Input id="fr" type="date" value={fechaRegistro} onChange={(e) => setFechaRegistro(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sk">Soporte de registro (key, opcional)</Label>
            <Input id="sk" value={soporteRegistroKey} onChange={(e) => setSoporteRegistroKey(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: cumplimiento --------------------------------------------------

function ModalCumplimiento({ sancion, onClose, onListo }: {
  sancion: SancionResumen; onClose: () => void; onListo: () => void;
}) {
  const [cumplida, setCumplida] = useState<boolean>(!!val<boolean>(sancion, 'cumplida'));
  const [fechaCumplimiento, setFechaCumplimiento] = useState('');
  const [tipoCumplimiento, setTipoCumplimiento] = useState<TipoCumplimiento>('TOTAL');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await cumplimientoSancion(idSancion(sancion), {
        cumplida,
        ...(fechaCumplimiento ? { fechaCumplimiento } : {}),
        ...(cumplida ? { tipoCumplimiento } : {}),
      });
      avisoExito('Cumplimiento actualizado.');
      onListo();
    } catch (err) {
      avisoError('No se pudo registrar el cumplimiento', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="size-5 text-primary" /> Cumplimiento</DialogTitle>
          <DialogDescription>Sanción: {val(sancion, 'tipo_sancion', 'tipoSancion') ?? '—'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cumplida} onChange={(e) => setCumplida(e.target.checked)} className="size-4 accent-primary" />
            Cumplida
          </label>
          {cumplida && (
            <div className="space-y-2">
              <Label>Tipo de cumplimiento</Label>
              <Select value={tipoCumplimiento} onValueChange={(v) => setTipoCumplimiento(v as TipoCumplimiento)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_CUMPLIMIENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="fcu">Fecha de cumplimiento</Label>
            <Input id="fcu" type="date" value={fechaCumplimiento} onChange={(e) => setFechaCumplimiento(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: archivar definitivamente --------------------------------------

function ModalArchivar({ exp, onClose, onListo }: {
  exp: ExpDetalle; onClose: () => void; onListo: () => void;
}) {
  const [fecha, setFecha] = useState(hoy());
  const [actaKey, setActaKey] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!fecha) { avisoError('Indique la fecha de archivo.'); return; }
    setGuardando(true);
    try {
      await archivarDefinitivo(exp.id, {
        fecha,
        ...(actaKey.trim() ? { actaKey: actaKey.trim() } : {}),
      });
      avisoExito('Expediente archivado definitivamente.');
      onListo();
    } catch (err) {
      avisoError('No se pudo archivar el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="size-5 text-primary" /> Archivar definitivamente</DialogTitle>
          <DialogDescription>
            Radicado: <strong>{exp.radicado}</strong>. Requiere cumplir los requisitos procesales
            (el backend lo bloquea con error 422 si faltan).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fa">Fecha de archivo</Label>
            <Input id="fa" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="aka">Acta de archivo (key, opcional)</Label>
            <Input id="aka" value={actaKey} onChange={(e) => setActaKey(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Archivando…' : 'Archivar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
